/**
 * Marketing / screenshot fixtures — St. Louis three-season room conversion.
 * Used only by `/debug/marketing-sandbox` and `?marketingSandbox=1` (non-prod).
 */

import { CLUMSE_SEARCH_FEE_USD } from "./procurementPricing.js";

export const MARKETING_SANDBOX_JOB_ID = "marketing_stl_three_season";
export const MARKETING_SANDBOX_THREAD_ID = "t_marketing_three_season";

export const MARKETING_PHASES = Object.freeze([
  "intake",
  "summary_review",
  "live_bids",
]);

const DEMO_PLACEHOLDER_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

const FINAL_DETAILS_QUESTION =
  "Anything else about the project we should factor in before we line up vendor quotes?";

export function buildMarketingStructuredScope(phase) {
  const base = {
    version: "1",
    zipCode: "63122",
    propertyType: "townhome",
    occupancy: "occupied",
    timeline: "researching",
    finishes: "mid-range",
    size: "medium",
    roomType: "patio",
    projectType: "remodel",
    projectSummary:
      "Three-season room conversion on an existing rear patio enclosure in a Kirkwood townhome. Convert the screened porch into a heated/cooled three-season room with Eze-Breeze-style vinyl window panels, insulated knee wall, LVP flooring over the existing slab, dedicated 20-amp circuit for a ceiling fan and outlet, and trim to match existing siding. Homeowner prefers mid-range finishes and will remain in the home during work. Also noted: vaulted ceiling detail at the ridge and a new exterior door to the side yard.",
    estimatedCost: {
      summaryLine:
        "Parts $8,200–$11,400 · Labor $14,800–$19,600 (St. Louis area ranges, not a firm quote)",
    },
    detailedTasks: [],
  };

  if (phase === "intake") {
    return {
      ...base,
      projectSummary:
        "Three-season room conversion on a rear patio enclosure — vinyl window panels, insulated knee wall, LVP flooring, electrical for fan/outlets, and trim to match existing siding.",
      timeline: undefined,
      finishes: undefined,
    };
  }

  return base;
}

export function buildMarketingProcurement(phase) {
  if (phase === "intake") return null;

  const scope = buildMarketingStructuredScope(
    phase === "summary_review" ? "summary_review" : "live_bids"
  );

  if (phase === "summary_review") {
    return {
      procurementPlanReady: true,
      scopeDiagnosisReady: true,
      procurementPlanMissing: [],
      dispatchOrchestrationFeeUsd: CLUMSE_SEARCH_FEE_USD,
      dispatchOrchestrationIncluded: false,
      clumseDispatchStatus: "PENDING",
      structuredScope: scope,
      lockedBenchmarkUsd: 24500,
    };
  }

  return {
    procurementPlanReady: true,
    scopeDiagnosisReady: true,
    procurementPlanMissing: [],
    dispatchOrchestrationFeeUsd: CLUMSE_SEARCH_FEE_USD,
    dispatchOrchestrationIncluded: false,
    clumseDispatchStatus: "DISPATCHED",
    structuredScope: scope,
    lockedBenchmarkUsd: 24500,
  };
}

export function buildMarketingSandboxBids() {
  const iso = (minsAgo) =>
    new Date(Date.now() - minsAgo * 60_000).toISOString();
  const title =
    "Three-season room — Kirkwood townhome (63122)";

  return [
    {
      id: "ms_bid_pending_1",
      ticketId: MARKETING_SANDBOX_JOB_ID,
      ticketTitle: title,
      vendorName: "Gateway Sunrooms & Patio Enclosures",
      googlePlaceRating: 4.7,
      googleReviewCount: 186,
      bidAmount: 28400,
      status: "pending",
      createdAt: iso(45),
      distanceMiles: 4.8,
      vendorAvailability: "Crew available in 2–3 weeks; Tue/Thu site visits.",
      timeline: "5–7 install days after materials arrive.",
      vendorChatSummary:
        "Quoted **Eze-Breeze VC600** panels on three sides, insulated knee wall, and LVP over existing slab. Includes permit pull for St. Louis County and **one return visit** for panel adjustment after first cold snap.",
    },
    {
      id: "ms_bid_accepted",
      ticketId: MARKETING_SANDBOX_JOB_ID,
      ticketTitle: title,
      vendorName: "ArchView Patio Enclosures LLC",
      googlePlaceRating: 4.9,
      googleReviewCount: 241,
      bidAmount: 31200,
      status: "accepted",
      createdAt: iso(120),
      distanceMiles: 6.2,
      vendorEmail: "estimating@archview-patio.example",
      propertyExactAddress: "1247 Claymont Dr, Kirkwood, MO 63122",
      vendorAvailability: "Start window: Nov 4–15; lead carpenter texts 30 min out.",
      timeline: "6 install days; electrical sub scheduled day 3.",
      vendorChatSummary:
        "**Locked scope** includes vaulted ceiling tie-in, side-yard exterior door, and upgraded fan-rated box. COI on file; **10-year workmanship** warranty noted in bid packet.",
    },
    {
      id: "ms_bid_premium",
      ticketId: MARKETING_SANDBOX_JOB_ID,
      ticketTitle: title,
      vendorName: "Premium Outdoor Living STL",
      googlePlaceRating: 4.8,
      googleReviewCount: 94,
      bidAmount: 38900,
      status: "pending",
      createdAt: iso(18),
      distanceMiles: 9.1,
      vendorAvailability: "Premium line — earliest slot in 4 weeks.",
      timeline: "8–10 days on site with dedicated project manager.",
      vendorChatSummary:
        "Premium package: **thermally broken aluminum** track system, **hardwood ceiling** option at ridge, integrated LED soffit, and **daily cleanup**. Price reflects white-glove occupied-home protocol.",
    },
  ];
}

function buildIntakeMessages() {
  const t = (minsAgo) =>
    new Date(Date.now() - minsAgo * 60_000).toISOString();

  return [
    {
      id: "ms_u1",
      role: "user",
      content:
        "We want to convert our back screened porch into a three-season room — vinyl window panels, better insulation, new floor, and electrical for a fan.",
      images: [DEMO_PLACEHOLDER_IMAGE],
      timestamp: t(720),
    },
    {
      id: "ms_a1",
      role: "assistant",
      content:
        "Got it — a **three-season room conversion** on your existing porch structure. That usually means window panels (like Eze-Breeze), floor upgrade, and electrical.\n\nWhat's your **ZIP code** so I can anchor labor to the St. Louis market?",
      timestamp: t(715),
    },
    {
      id: "ms_u2",
      role: "user",
      content: "63122 — Kirkwood townhome.",
      timestamp: t(700),
    },
    {
      id: "ms_a2",
      role: "assistant",
      content:
        "Thanks. Is this a **single-family home, townhome, condo, or multi-family** property?",
      timestamp: t(698),
    },
    {
      id: "ms_u3",
      role: "user",
      content: "Townhome.",
      timestamp: t(690),
    },
    {
      id: "ms_a3",
      role: "assistant",
      content:
        "Will anyone be **living there during the work**, or is the unit vacant?",
      timestamp: t(688),
    },
    {
      id: "ms_u4",
      role: "user",
      content: "We'll be living there — it's our primary home.",
      timestamp: t(680),
    },
    {
      id: "ms_a4",
      role: "assistant",
      content:
        "Timeline: **urgent**, **within 30 days**, or **still planning**?",
      timestamp: t(678),
    },
  ];
}

function buildSummaryReviewMessages() {
  const t = (minsAgo) =>
    new Date(Date.now() - minsAgo * 60_000).toISOString();
  const scope = buildMarketingStructuredScope("summary_review");

  return [
    ...buildIntakeMessages().slice(0, -1),
    {
      id: "ms_u4",
      role: "user",
      content: "Still planning — hoping to start before winter.",
      timestamp: t(680),
    },
    {
      id: "ms_a5",
      role: "assistant",
      content:
        "**Project Blueprint Ready**\n\nFor a **medium three-season conversion** in 63122, homeowners typically see **$22k–$32k** all-in depending on panel system and electrical scope. Comparing three fixed bids usually saves more than the search fee.\n\n" +
        FINAL_DETAILS_QUESTION,
      timestamp: t(660),
      metadata: { diagnosisComplete: true },
    },
    {
      id: "ms_u5",
      role: "user",
      content:
        "Also want a vaulted ceiling at the ridge and a new exterior door to the side yard.",
      timestamp: t(640),
    },
    {
      id: "ms_a6",
      role: "assistant",
      content: FINAL_DETAILS_QUESTION,
      timestamp: t(638),
    },
    {
      id: "ms_u6",
      role: "user",
      content: "Nope that's it.",
      timestamp: t(620),
    },
    {
      id: "ms_a7",
      role: "assistant",
      content:
        "Your scope is locked in the **Vendor Quotes** panel. On a job this size, comparing three local bids usually saves more than the one-time search fee.\n\nPay $49 · Start search",
      timestamp: t(600),
      metadata: {
        intakeReadyForSearch: true,
        summaryReviewLocked: true,
        intakePhase: "summary_review",
        checkoutFeeUsd: CLUMSE_SEARCH_FEE_USD,
        structuredScope: scope,
      },
    },
  ];
}

function buildLiveBidsMessages() {
  const summaryMsgs = buildSummaryReviewMessages();
  const last = summaryMsgs[summaryMsgs.length - 1];
  return [
    ...summaryMsgs.slice(0, -1),
    {
      ...last,
      content:
        "Vendor search is live — **3 bids** are in your panel. ArchView Patio Enclosures is marked **accepted**; Gateway Sunrooms and Premium Outdoor Living STL are still pending review.\n\nOpen **Vendor Quotes** to compare line items and availability.",
      metadata: {
        ...last.metadata,
        dispatchSearchStarted: true,
      },
    },
  ];
}

/**
 * @param {"intake"|"summary_review"|"live_bids"} phase
 */
export function buildMarketingSandboxThread(phase) {
  const messages =
    phase === "intake"
      ? buildIntakeMessages()
      : phase === "summary_review"
        ? buildSummaryReviewMessages()
        : buildLiveBidsMessages();

  const linkedTicketId =
    phase === "intake" ? null : MARKETING_SANDBOX_JOB_ID;
  const dispatched = phase === "live_bids";

  return {
    id: MARKETING_SANDBOX_THREAD_ID,
    title: "Three-season room — Kirkwood (63122)",
    slug: "three-season-room-kirkwood",
    updatedAt: Date.now(),
    linkedTicketId,
    dispatched,
    __demo: true,
    __marketingSandbox: true,
    messages,
  };
}

export function getMarketingSandboxThreads(phase) {
  return [buildMarketingSandboxThread(phase)];
}

export function normalizeMarketingPhase(raw) {
  const p = String(raw || "live_bids").toLowerCase().replace(/-/g, "_");
  return MARKETING_PHASES.includes(p) ? p : "live_bids";
}
